import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { BsSearch, BsHeart, BsCart3, BsPerson, BsList, BsX } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { getAProduct } from "../features/products/productSlilce";
import { getUserCart, resetState } from "../features/user/userSlice";
import { getCategories } from "../features/category/categorySlice";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";
import { useTranslation } from "react-i18next";
import axios from 'axios';

const Header = () => {
  const dispatch = useDispatch();
  const authCartState = useSelector((state) => state?.auth?.cartProducts);
  const guestCartState = useSelector((state) => state?.auth?.guestCart);
  const authState = useSelector((state) => state?.auth);
  const productState = useSelector((state) => state?.product?.product);
  
  // Use the hook at the top level
  const { currentLanguage, supportedLanguages, changeLanguage } = useLanguage();
  const { t } = useTranslation();

  const [productOpt, setProductOpt] = useState([]);
  const [paginate, setPaginate] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigate = useNavigate();

  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config2 = {
    headers: {
      Authorization: `Bearer ${
        getTokenFromLocalStorage !== null ? getTokenFromLocalStorage.token : ""
      }`,
      Accept: "application/json",
    },
  };

  // Check if user is authenticated by verifying token exists and is likely valid
  const isAuthenticated = () => {
    if (!getTokenFromLocalStorage || !getTokenFromLocalStorage.token) {
      return false;
    }
    // Additional check for token expiration
    return !isTokenExpired(getTokenFromLocalStorage.token);
  };
  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      return true;
    }
  };

  // Auto logout function that mimics manual logout
  const autoLogout = () => {
    localStorage.clear();
    dispatch(resetState());
    window.location.reload();
  };
  // Get current cart items (either auth cart or guest cart)
  const getCurrentCartItems = () => {
    if (isAuthenticated()) {
      return authCartState;
    } else {
      return guestCartState;
    }
  };

  useEffect(() => {
    if (isAuthenticated()) {
      dispatch(getUserCart(config2));
    } else {
      dispatch(getUserCart()); // For guest users
    }
  }, [authState?.user]);

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  useEffect(() => {
    let data = [];
    for (let index = 0; index < productState?.length; index++) {
      const element = productState[index];
      data.push({ id: index, prod: element?._id, name: element?.title });
    }
    setProductOpt(data);
  }, [productState]);

  // Add axios interceptor to handle 401 errors globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Check if it's a 401 error indicating session expiration
        if (error.response?.status === 401) {
          // Check if it's specifically a token expiration or session invalidation error
          const message = error.response?.data?.message || '';
          if (message.includes('Access token expired') || 
              message.includes('Session invalidated') ||
              message.includes('Session expired')) {
            autoLogout();
          }
        }
        return Promise.reject(error);
      }
    );

    // Clean up interceptor
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Periodically check for token expiration
  useEffect(() => {
    // Only run if user is authenticated
    if (isAuthenticated() && getTokenFromLocalStorage?.token) {
      // Check immediately on mount
      if (isTokenExpired(getTokenFromLocalStorage.token)) {
        autoLogout();
        return;
      }

      // Set up interval to check every minute
      const interval = setInterval(() => {
        if (isTokenExpired(getTokenFromLocalStorage.token)) {
          autoLogout();
        }
      }, 60000); // Check every minute

      // Clean up interval on unmount
      return () => clearInterval(interval);
    }
  }, [getTokenFromLocalStorage]);
  const handleLogout = () => {
    localStorage.clear();
    dispatch(resetState());
    window.location.reload();
  };
  return (
    // Added kids-header class to use kid-friendly fonts
    <header className="w-full bg-white border-b shadow-sm sticky top-0 z-50 kids-header">
      {/* MOBILE HEADER */}
      <div className="flex items-center justify-between px-4 py-3 md:hidden">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src="/veda-logo-final.png" alt="VEDA Logo" className="h-10" />
        </Link>

        <div className="flex items-center gap-4">
          {/* Language Selector - Mobile */}
          <LanguageSelector />
          
          {/* Profile */}
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="text-gray-800 hover:text-blue-500"
          >
            <BsPerson size={22} />
          </button>
          {/* If logged in */}
          {isDropdownOpen && isAuthenticated() && (
            <div className="absolute right-10 mt-48 w-40 bg-white border rounded shadow-lg py-2 z-50">
              <Link
                to="/my-profile"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t('header.myProfile')}
              </Link>
              <Link
                to="/my-orders"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t('header.myOrders')}
              </Link>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                {t('header.logout')}
              </button>
            </div>
          )}

          {/* If logged out */}
          {isDropdownOpen && !isAuthenticated() && (
            <div className="absolute right-10 mt-48 w-32 bg-white border rounded shadow-lg py-2 z-50">
              <Link
                to="/login"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setIsDropdownOpen(false)}
              >
                {t('header.signIn')}
              </Link>
            </div>
          )}

          {/* Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-gray-800 hover:text-blue-500"
          >
            {isMobileMenuOpen ? <BsX size={26} /> : <BsList size={26} />}
          </button>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:block">
        <div className="max-w-7xl mx-auto px-6">
          {/* Main Header Row */}
          <div className="flex items-center justify-between py-4">
            {/* Left: Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img src="/veda-logo-final.png" alt="VEDA Logo" className="h-12" />
            </Link>

            {/* Center: Search Bar */}
            <div className="flex-1 max-w-2xl mx-8">
              <div className="relative">
                <Typeahead
                  id="desktop-search"
                  onChange={(selected) => {
                    if (selected?.length > 0) {
                      navigate(`/product/${selected[0]?.prod}`);
                      dispatch(getAProduct(selected[0]?.prod));
                    }
                  }}
                  options={productOpt}
                  paginate={paginate}
                  labelKey={"name"}
                  placeholder={t('common.search')}
                  className="w-full"
                />
                <BsSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black" size={18} />
                <style jsx>{`
                  .rbt-input-main {
                    border: 2px solid #e5e7eb !important;
                    border-radius: 50px !important;
                    padding: 12px 16px 12px 48px !important;
                    font-size: 14px !important;
                    background-color: #f9fafb !important;
                    transition: all 0.2s ease !important;
                    box-shadow: none !important;
                  }
                  .rbt-input-main:focus {
                    border-color: #ef4444 !important;
                    background-color: white !important;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
                    outline: none !important;
                  }
                  .rbt-menu {
                    border-radius: 12px !important;
                    border: 1px solid #e5e7eb !important;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
                    margin-top: 4px !important;
                  }
                  .rbt-menu-item {
                    padding: 12px 16px !important;
                    border-bottom: 1px solid #f3f4f6 !important;
                  }
                  .rbt-menu-item:hover {
                    background-color: #f3f4f6 !important;
                  }
                  .rbt-menu-item.active {
                    background-color: #fef2f2 !important;
                    color: #dc2626 !important;
                  }
                `}</style>
              </div>
            </div>

            {/* Right: Icons */}
            <div className="flex items-center gap-6">
              {/* Language Selector - Desktop */}
              <LanguageSelector />

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-500 transition-all duration-200"
                >
                  <BsPerson size={20} />
                </button>

                {/* If logged in */}
                {isDropdownOpen && isAuthenticated() && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
                    <Link
                      to="/my-profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {t('header.myProfile')}
                    </Link>
                    <Link
                      to="/my-orders"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {t('header.myOrders')}
                    </Link>
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      {t('header.logout')}
                    </button>
                  </div>
                )}

                {/* If logged out */}
                {isDropdownOpen && !isAuthenticated() && (
                  <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
                    <Link
                      to="/login"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {t('header.signIn')}
                    </Link>
                  </div>
                )}
              </div>

              <Link 
                to="/wishlist" 
                className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-500 transition-all duration-200"
              >
                <BsHeart size={20} />
              </Link>
              
              <Link 
                to="/cart" 
                className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-500 transition-all duration-200"
              >
                <BsCart3 size={20} />
                {getCurrentCartItems()?.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                    {getCurrentCartItems()?.length}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Navigation Row */}
          <div className="border-t border-gray-100">
            <nav className="flex items-center justify-center gap-12 py-4">
              <NavLink 
                to="/products" 
                className={({ isActive }) => 
                  `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                    isActive 
                      ? "text-red-500 border-b-2 border-red-500 pb-1" 
                      : "text-gray-600 hover:text-red-500"
                  }`
                }
              >
                {t('header.toys')}
              </NavLink>
              <NavLink 
                to="/games" 
                className={({ isActive }) => 
                  `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                    isActive 
                      ? "text-red-500 border-b-2 border-red-500 pb-1" 
                      : "text-gray-600 hover:text-red-500"
                  }`
                }
              >
                {t('header.games')}
              </NavLink>
              <NavLink 
                to="/puzzles" 
                className={({ isActive }) => 
                  `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                    isActive 
                      ? "text-red-500 border-b-2 border-red-500 pb-1" 
                      : "text-gray-600 hover:text-red-500"
                  }`
                }
              >
                {t('header.puzzles')}
              </NavLink>
              <NavLink 
                to="/videos" 
                className={({ isActive }) => 
                  `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                    isActive 
                      ? "text-red-500 border-b-2 border-red-500 pb-1" 
                      : "text-gray-600 hover:text-red-500"
                  }`
                }
              >
                {t('header.videos')}
              </NavLink>
              <NavLink 
                to="/virtual-temples" 
                className={({ isActive }) => 
                  `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                    isActive 
                      ? "text-red-500 border-b-2 border-red-500 pb-1" 
                      : "text-gray-600 hover:text-red-500"
                  }`
                }
              >
                {t('header.virtualTemples')}
              </NavLink>
            </nav>
          </div>
        </div>
      </div>

      {/* MOBILE DROPDOWN MENU */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t bg-white shadow-lg">
          {/* Search */}
          <div className="px-4 py-3">
            <div className="relative w-full">
              <Typeahead
                id="mobile-search"
                onChange={(selected) => {
                  if (selected?.length > 0) {
                    navigate(`/product/${selected[0]?.prod}`);
                    dispatch(getAProduct(selected[0]?.prod));
                    setIsMobileMenuOpen(false);
                  }
                }}
                options={productOpt}
                paginate={paginate}
                labelKey={"name"}
                placeholder={t('common.search')}
                className="w-full bg-gray-100 border-none rounded-lg pl-12 pr-4 py-2 text-gray-700 placeholder-gray-500 focus:ring-0"
              />
              <BsSearch className="absolute left-3 top-3 text-gray-500" />
            </div>
          </div>

          {/* Links */}
          <nav className="flex flex-col gap-3 px-4 py-3 border-b">
            <NavLink 
              to="/products" 
              className={({ isActive }) => 
                `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                  isActive 
                    ? "text-red-500" 
                    : "text-gray-600 hover:text-red-500"
                }`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('header.toys')}
            </NavLink>
            <NavLink 
              to="/games" 
              className={({ isActive }) => 
                `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                  isActive 
                    ? "text-red-500" 
                    : "text-gray-600 hover:text-red-500"
                }`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('header.games')}
            </NavLink>
            <NavLink 
              to="/puzzles" 
              className={({ isActive }) => 
                `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                  isActive 
                    ? "text-red-500" 
                    : "text-gray-600 hover:text-red-500"
                }`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('header.puzzles')}
            </NavLink>
            <NavLink 
              to="/videos" 
              className={({ isActive }) => 
                `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                  isActive 
                    ? "text-red-500" 
                    : "text-gray-600 hover:text-red-500"
                }`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('header.videos')}
            </NavLink>
            <NavLink 
              to="/virtual-temples" 
              className={({ isActive }) => 
                `font-medium text-sm uppercase tracking-wider transition-colors duration-200 ${
                  isActive 
                    ? "text-red-500" 
                    : "text-gray-600 hover:text-red-500"
                }`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('header.virtualTemples')}
            </NavLink>
          </nav>

          {/* Bottom: Cart and Wishlist */}
          <div className="flex border-t">
            <Link 
              to="/wishlist" 
              className="flex-1 flex items-center justify-center gap-2 py-4 text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <BsHeart size={20} />
              <span className="font-medium">{t('header.wishlist')}</span>
            </Link>
            <Link 
              to="/cart" 
              className="flex-1 flex items-center justify-center gap-2 py-4 text-gray-700 hover:bg-gray-50 transition-colors relative"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <BsCart3 size={20} />
              <span className="font-medium">{t('header.cart')}</span>
              {getCurrentCartItems()?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {getCurrentCartItems()?.length}
                </span>
              )}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;